import { createContext, useContext, useState, useEffect } from 'react';
import { settingsApi } from '@/lib/api';

// Font presets
export const FONTS = {
  'classic': {
    name: 'Klassisch (Standard)',
    heading: "'Cormorant Garamond', serif",
    body: "'Manrope', sans-serif",
    description: 'Elegante Serifenschrift für Überschriften'
  },
  'modern': {
    name: 'Modern Clean',
    heading: "'Inter', sans-serif",
    body: "'Inter', sans-serif",
    description: 'Klare, moderne Sans-Serif'
  },
  'elegant': {
    name: 'Elegant Playfair',
    heading: "'Playfair Display', serif",
    body: "'Raleway', sans-serif",
    description: 'Luxuriöse Kombination'
  },
  'bold': {
    name: 'Bold & Strong',
    heading: "'Montserrat', sans-serif",
    body: "'Montserrat', sans-serif",
    description: 'Kräftige, selbstbewusste Schrift'
  },
  'sporty': {
    name: 'Sportlich',
    heading: "'Oswald', sans-serif",
    body: "'Roboto', sans-serif",
    description: 'Kondensierte Headlines, dynamisch'
  },
  'minimal': {
    name: 'Minimalistisch',
    heading: "'DM Sans', sans-serif",
    body: "'DM Sans', sans-serif",
    description: 'Schlicht und zeitlos'
  },
  'futuristic': {
    name: 'Futuristisch',
    heading: "'Space Grotesk', sans-serif",
    body: "'Space Grotesk', sans-serif",
    description: 'Technik-inspiriert'
  },
  'impact': {
    name: 'Impact Headlines',
    heading: "'Bebas Neue', sans-serif",
    body: "'Poppins', sans-serif",
    description: 'Große, auffällige Überschriften'
  },
  'soft': {
    name: 'Soft & Friendly',
    heading: "'Poppins', sans-serif",
    body: "'Poppins', sans-serif",
    description: 'Runde, freundliche Formen'
  },
  'professional': {
    name: 'Business Professional',
    heading: "'Raleway', sans-serif",
    body: "'Roboto', sans-serif",
    description: 'Seriös und professionell'
  }
};

// Theme presets with both hex and HSL values for Tailwind
export const THEMES = {
  classic: {
    name: 'Klassisch Elegant',
    description: 'Goldakzente auf hellem Hintergrund',
    colors: {
      primary: '#D4AF37',
      secondary: '#0A0A0A',
      accent: '#D4AF37',
      background: '#F9F9F7',
      backgroundDark: '#0A0A0A',
      surface: '#FFFFFF',
      surfaceDark: '#1A1A1A',
      text: '#0A0A0A',
      textDark: '#FFFFFF',
      textMuted: '#666666',
      textMutedDark: '#999999',
      border: '#E5E5E5',
      borderDark: '#333333'
    },
    hsl: {
      background: '48 14% 97%',
      backgroundDark: '0 0% 4%',
      foreground: '0 0% 4%',
      foregroundDark: '0 0% 100%',
      card: '0 0% 100%',
      cardDark: '0 0% 10%',
      muted: '0 0% 96%',
      mutedDark: '0 0% 10%',
      mutedForeground: '0 0% 40%',
      mutedForegroundDark: '0 0% 60%',
      border: '0 0% 90%',
      borderDark: '0 0% 20%'
    }
  },
  'modern-dark': {
    name: 'Modern Dunkel',
    description: 'Elegantes dunkles Design mit Goldakzenten',
    colors: {
      primary: '#D4AF37',
      secondary: '#FFFFFF',
      accent: '#D4AF37',
      background: '#0F0F0F',
      backgroundDark: '#0F0F0F',
      surface: '#1A1A1A',
      surfaceDark: '#1A1A1A',
      text: '#FFFFFF',
      textDark: '#FFFFFF',
      textMuted: '#999999',
      textMutedDark: '#999999',
      border: '#333333',
      borderDark: '#333333'
    },
    hsl: {
      background: '0 0% 6%',
      backgroundDark: '0 0% 6%',
      foreground: '0 0% 100%',
      foregroundDark: '0 0% 100%',
      card: '0 0% 10%',
      cardDark: '0 0% 10%',
      muted: '0 0% 10%',
      mutedDark: '0 0% 10%',
      mutedForeground: '0 0% 60%',
      mutedForegroundDark: '0 0% 60%',
      border: '0 0% 20%',
      borderDark: '0 0% 20%'
    }
  },
  'neon-sport': {
    name: 'Neon Sport',
    description: 'Sportliches Design mit Neon-Grün',
    colors: {
      primary: '#00FF88',
      secondary: '#FFFFFF',
      accent: '#00FF88',
      background: '#0A0A0A',
      backgroundDark: '#0A0A0A',
      surface: '#141414',
      surfaceDark: '#141414',
      text: '#FFFFFF',
      textDark: '#FFFFFF',
      textMuted: '#888888',
      textMutedDark: '#888888',
      border: '#2A2A2A',
      borderDark: '#2A2A2A'
    },
    hsl: {
      background: '0 0% 4%',
      backgroundDark: '0 0% 4%',
      foreground: '0 0% 100%',
      foregroundDark: '0 0% 100%',
      card: '0 0% 8%',
      cardDark: '0 0% 8%',
      muted: '0 0% 8%',
      mutedDark: '0 0% 8%',
      mutedForeground: '0 0% 53%',
      mutedForegroundDark: '0 0% 53%',
      border: '0 0% 17%',
      borderDark: '0 0% 17%'
    }
  }
};

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
    // Check localStorage for user preference
    const savedMode = localStorage.getItem('theme_mode');
    if (savedMode) {
      setDarkMode(savedMode === 'dark');
    }
  }, []);

  useEffect(() => {
    // Apply theme to document
    if (settings) {
      applyTheme();
    }
  }, [settings, darkMode]);

  const loadSettings = async () => {
    try {
      const response = await settingsApi.get();
      setSettings(response.data);
      
      // Set default mode from settings if no user preference
      const savedMode = localStorage.getItem('theme_mode');
      if (!savedMode && response.data.design_default_mode) {
        setDarkMode(response.data.design_default_mode === 'dark');
      }
    } catch (error) {
      console.error('Error loading theme settings:', error);
      setSettings({});
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = () => {
    const theme = THEMES.classic; // Always use classic as base
    const root = document.documentElement;
    
    // Custom colors override - these are the main design colors
    const primaryColor = settings?.design_primary_color || theme.colors.primary;
    const accentColor = settings?.design_accent_color || theme.colors.accent;
    const secondaryColor = settings?.design_secondary_color || theme.colors.secondary;
    
    // Convert hex to HSL for Tailwind CSS variables
    const hexToHSL = (hex) => {
      let r = 0, g = 0, b = 0;
      if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
      } else if (hex.length === 7) {
        r = parseInt(hex.slice(1, 3), 16);
        g = parseInt(hex.slice(3, 5), 16);
        b = parseInt(hex.slice(5, 7), 16);
      }
      r /= 255; g /= 255; b /= 255;
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;
      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
          default: h = 0;
        }
      }
      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };
    
    // Dark mode is controlled by settings and user toggle
    const isDark = darkMode;
    
    // Apply CSS variables for custom styling
    root.style.setProperty('--color-primary', primaryColor);
    root.style.setProperty('--color-accent', accentColor);
    root.style.setProperty('--color-secondary', secondaryColor);
    
    // Convert primary color to HSL for Tailwind accent/ring
    const primaryHSL = hexToHSL(primaryColor);
    root.style.setProperty('--accent', primaryHSL);
    root.style.setProperty('--ring', primaryHSL);
    
    if (isDark) {
      // Dark mode colors
      root.style.setProperty('--color-background', '#0A0A0A');
      root.style.setProperty('--color-surface', '#1A1A1A');
      root.style.setProperty('--color-text', '#FFFFFF');
      root.style.setProperty('--color-text-muted', '#999999');
      root.style.setProperty('--color-border', '#333333');
      
      // Apply Tailwind HSL variables for dark mode
      root.style.setProperty('--background', '0 0% 4%');
      root.style.setProperty('--foreground', '0 0% 100%');
      root.style.setProperty('--card', '0 0% 10%');
      root.style.setProperty('--card-foreground', '0 0% 100%');
      root.style.setProperty('--popover', '0 0% 10%');
      root.style.setProperty('--popover-foreground', '0 0% 100%');
      root.style.setProperty('--muted', '0 0% 10%');
      root.style.setProperty('--muted-foreground', '0 0% 60%');
      root.style.setProperty('--border', '0 0% 20%');
      root.style.setProperty('--input', '0 0% 20%');
      root.style.setProperty('--accent-foreground', '0 0% 100%');
      
      root.classList.add('dark');
    } else {
      // Light mode colors
      root.style.setProperty('--color-background', '#F9F9F7');
      root.style.setProperty('--color-surface', '#FFFFFF');
      root.style.setProperty('--color-text', '#0A0A0A');
      root.style.setProperty('--color-text-muted', '#666666');
      root.style.setProperty('--color-border', '#E5E5E5');
      
      // Apply Tailwind HSL variables for light mode
      root.style.setProperty('--background', '48 14% 97%');
      root.style.setProperty('--foreground', '0 0% 4%');
      root.style.setProperty('--card', '0 0% 100%');
      root.style.setProperty('--card-foreground', '0 0% 4%');
      root.style.setProperty('--popover', '0 0% 100%');
      root.style.setProperty('--popover-foreground', '0 0% 4%');
      root.style.setProperty('--muted', '0 0% 96%');
      root.style.setProperty('--muted-foreground', '0 0% 40%');
      root.style.setProperty('--border', '0 0% 90%');
      root.style.setProperty('--input', '0 0% 90%');
      root.style.setProperty('--accent-foreground', '0 0% 4%');
      
      root.classList.remove('dark');
    }
    
    // Apply font settings
    const fontPreset = settings?.design_font || 'classic';
    const font = FONTS[fontPreset] || FONTS.classic;
    root.style.setProperty('--font-heading', font.heading);
    root.style.setProperty('--font-body', font.body);
  };

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('theme_mode', newMode ? 'dark' : 'light');
  };

  const isDarkModeAllowed = () => {
    // Check if dark mode toggle is enabled in settings
    return settings?.design_dark_mode_enabled !== false;
  };

  const isInherentlyDark = () => {
    // No longer using theme presets, so never inherently dark
    // Dark mode is controlled by user toggle and default setting
    return false;
  };

  return (
    <ThemeContext.Provider value={{ 
      settings, 
      darkMode, 
      toggleDarkMode, 
      loading,
      isDarkModeAllowed,
      isInherentlyDark,
      refreshSettings: loadSettings,
      currentTheme: 'classic'
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
